import type { useTranslate } from "@similarweb/i18n-provider";
import type { ProWebsiteModelsConversionRatesDtoCategoriesConversionRatesPopOverview } from "@similarweb/pro-api-client";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { ChangeCell, ProgressBarWithValueCell, TextCell } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import { LegendBullet } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { CONVERSION_RATE_TRANSLATION_KEYS, conversionRateFormatters } from "../../consts";
import { comparisonRowAutomationId } from "../automationIds";
import type { IComparisonRow } from "../buildComparisonRows";

import { renderHeaderWithIndicator } from "./PeriodIndicatorHeader";
import { getStripedBackground } from "./stripedFill";

type Translate = ReturnType<typeof useTranslate>["translate"];
type Entry = ProWebsiteModelsConversionRatesDtoCategoriesConversionRatesPopOverview;
type PopRow = IComparisonRow<Entry>;

const NA_DASH = "—";

export const computeMaxConvertedVisits = (rows: PopRow[]): number =>
    rows.reduce((acc, row) => {
        const entry = row.entry;
        if (!entry) {
            return acc;
        }
        const a = entry.convertedVisits.originalValue ?? 0;
        const b = entry.convertedVisits.comparedValue ?? 0;
        return Math.max(acc, a, b);
    }, 0);

const fraction = (value: number | null | undefined, max: number): number => {
    if (value == null || max <= 0) {
        return 0;
    }
    return value / max;
};

const renderIndustry = (row: PopRow, automationPrefix: string) => (
    <Flex
        alignItems="center"
        gap="8px"
        data-automation={comparisonRowAutomationId(automationPrefix, row.id, "industry")}
    >
        <LegendBullet
            labelColor={row.color}
            text={
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[400]}
                >
                    {row.name}
                </Text>
            }
        />
    </Flex>
);

const renderVisitsCell = (row: PopRow, automationPrefix: string, period: "compared" | "current", maxValue: number) => {
    const entry = row.entry;
    const automationId = comparisonRowAutomationId(automationPrefix, row.id, `visits-${period}`);
    if (!entry) {
        return <TextCell dataAutomation={automationId}>{NA_DASH}</TextCell>;
    }
    const value = period === "current" ? entry.convertedVisits.originalValue : entry.convertedVisits.comparedValue;
    const fillColor = period === "compared" ? getStripedBackground(row.color) : row.color;
    return (
        <ProgressBarWithValueCell
            share={fraction(value, maxValue)}
            valueMinWidth="64px"
            color={fillColor as StrictColor}
            value={value == null ? NA_DASH : conversionRateFormatters.convertedVisits(value)}
        />
    );
};

const renderChange = (row: PopRow, automationPrefix: string) => {
    const change = row.entry?.convertedVisits.change;
    return (
        <Flex
            justifyContent="flex-end"
            data-automation={comparisonRowAutomationId(automationPrefix, row.id, "change")}
        >
            <ChangeCell
                value={change ?? undefined}
                formattedValue={change == null ? NA_DASH : conversionRateFormatters.visitsChange(change)}
                noDataSign={NA_DASH}
            />
        </Flex>
    );
};

const renderAvgRateCell = (row: PopRow, automationPrefix: string, period: "compared" | "current") => {
    const entry = row.entry;
    const value = !entry
        ? null
        : period === "current"
          ? entry.avgConversionRate.originalValue
          : entry.avgConversionRate.comparedValue;
    return (
        <Flex
            justifyContent="flex-end"
            data-automation={comparisonRowAutomationId(automationPrefix, row.id, `avg-rate-${period}`)}
        >
            <TextCell>{value == null ? NA_DASH : conversionRateFormatters.avgConversionRate(value)}</TextCell>
        </Flex>
    );
};

export const getComparisonOverviewPopColumns = (
    translate: Translate,
    automationPrefix: string,
    isYearOverYear: boolean,
    maxConvertedVisits: number,
): ColumnsType<PopRow> => [
    {
        key: "industry",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColIndustry),
        width: 270,
        render: (_, row) => renderIndustry(row, automationPrefix),
    },
    {
        key: "convertedVisitsCompared",
        align: "right",
        title: renderHeaderWithIndicator(
            translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColConvertedVisits),
            true,
        ),
        minWidth: 190,
        render: (_, row) => renderVisitsCell(row, automationPrefix, "compared", maxConvertedVisits),
    },
    {
        key: "convertedVisitsCurrent",
        align: "right",
        title: renderHeaderWithIndicator(
            translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColConvertedVisits),
            false,
        ),
        minWidth: 190,
        render: (_, row) => renderVisitsCell(row, automationPrefix, "current", maxConvertedVisits),
    },
    {
        key: "change",
        align: "right",
        title: translate(
            isYearOverYear
                ? CONVERSION_RATE_TRANSLATION_KEYS.comparisonColYoYChange
                : CONVERSION_RATE_TRANSLATION_KEYS.comparisonColPopChange,
        ),
        render: (_, row) => renderChange(row, automationPrefix),
        width: 120,
    },
    {
        key: "avgRateCompared",
        align: "right",
        title: renderHeaderWithIndicator(translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColAvgRate), true),
        render: (_, row) => renderAvgRateCell(row, automationPrefix, "compared"),
        width: 160,
    },
    {
        key: "avgRateCurrent",
        align: "right",
        title: renderHeaderWithIndicator(translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColAvgRate), false),
        render: (_, row) => renderAvgRateCell(row, automationPrefix, "current"),
        width: 160,
    },
];
